<!-- src/apps/secret/support/Pricing.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import { RouterLink, useRoute } from 'vue-router';
  import BasicFormAlerts from '@/shared/components/forms/BasicFormAlerts.vue';
  import PlanCardSkeleton from '@/shared/components/billing/PlanCardSkeleton.vue';
  import PlanCard from '@/shared/components/billing/PlanCard.vue';
  import FeedbackToggle from '@/shared/components/ui/FeedbackToggle.vue';
  import { classifyError } from '@/schemas/errors';
  import { BillingService, type Plan as BillingPlan } from '@/services/billing.service';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import type { BillingInterval } from '@/types/billing';
  import { computed, onMounted, ref, watch } from 'vue';

  const props = withDefaults(
    defineProps<{
      /** Display free plan as a standalone banner (true) or as a card in the grid (false) */
      freePlanStandalone?: boolean;
    }>(),
    {
      freePlanStandalone: true,
    }
  );

  const { t } = useI18n();
  const route = useRoute();
  const bootstrapStore = useBootstrapStore();
  const { authentication } = bootstrapStore;
  const signupEnabled = computed(
    () => authentication?.enabled && authentication?.signup
  );
  const signinEnabled = computed(
    () => authentication?.enabled && authentication?.signin
  );

  // Map URL interval slugs to internal billing interval
  // Supports: month, monthly -> 'month' and year, yearly, annual -> 'year'
  const INTERVAL_MAP: Record<string, BillingInterval> = {
    month: 'month',
    monthly: 'month',
    year: 'year',
    yearly: 'year',
    annual: 'year',
  };

  // Get billing interval from URL param or default to month
  const getInitialBillingInterval = (): BillingInterval => {
    const intervalParam = route.params.interval as string | undefined;
    if (intervalParam && INTERVAL_MAP[intervalParam.toLowerCase()]) {
      return INTERVAL_MAP[intervalParam.toLowerCase()];
    }
    return 'month';
  };

  // Get highlighted product from URL param (e.g., 'identity_plus', 'team_plus')
  // This will be matched against plan.id prefix
  const highlightedProduct = computed((): string | null => {
    const productParam = route.params.product as string | undefined;
    if (productParam) {
      return productParam.toLowerCase();
    }
    return null;
  });

  const billingInterval = ref<BillingInterval>(getInitialBillingInterval());
  const isLoadingPlans = ref(false);
  const error = ref('');

  // Watch for route param changes to update billing interval
  watch(
    () => route.params.interval,
    (newInterval) => {
      if (newInterval && typeof newInterval === 'string') {
        const mapped = INTERVAL_MAP[newInterval.toLowerCase()];
        if (mapped) {
          billingInterval.value = mapped;
        }
      }
    },
    { immediate: true }
  );

  // Plans loaded from API
  const plans = ref<BillingPlan[]>([]);

  // Filter plans by selected billing interval
  // When freePlanStandalone is true, exclude free plans (they show in banner)
  // When freePlanStandalone is false, include free plans in the grid
  const filteredPlans = computed(() =>
    plans.value.filter((plan) => {
      if (plan.tier === 'free') {
        return !props.freePlanStandalone;
      }
      return plan.interval === billingInterval.value;
    })
  );

  // Get the free plan (if available)
  const freePlan = computed(() =>
    plans.value.find((plan) => plan.tier === 'free')
  );

  /**
   * Check if plan should show "Most Popular" badge.
   * Uses API-provided is_popular flag.
   */
  const isPlanRecommended = (plan: BillingPlan): boolean => plan.is_popular === true;

  /**
   * Check if plan should be highlighted based on URL product parameter.
   * Matches the product param against the plan ID prefix.
   * Example: product='identity_plus' matches plan.id='identity_plus_v1'
   */
  const isPlanHighlighted = (plan: BillingPlan): boolean => {
    if (!highlightedProduct.value) return false;
    return plan.id.toLowerCase().startsWith(highlightedProduct.value);
  };

  /**
   * Determine CTA text based on plan tier
   */
  const getCtaLabel = (plan: BillingPlan): string => {
    if (plan.tier === 'free') return t('web.pricing.get_started_free');
    return t('web.pricing.start_trial');
  };

  /**
   * Get the interval name for query params from plan interval.
   * Uses 'monthly' or 'yearly' format for URL query params.
   */
  const getIntervalForQuery = (plan: BillingPlan): string =>
    plan.interval === 'year' ? 'yearly' : 'monthly';

  /**
   * Build signup URL with product and interval query params.
   * Free plans go to /signup without billing params.
   * Paid plans include product and interval for checkout flow.
   * All variants include redirect=<current fullPath> so the user returns
   * to this pricing view (with any query/hash preserved) after authentication.
   */
  const getSignupUrl = (plan: BillingPlan): string => {
    const params = new URLSearchParams();
    if (plan.tier !== 'free') {
      params.set('product', plan.id);
      params.set('interval', getIntervalForQuery(plan));
    }
    params.set('redirect', route.fullPath);
    return `/signup?${params.toString()}`;
  };

  /**
   * Build signin URL preserving the current fullPath as a redirect target.
   */
  const signinUrl = computed(() => {
    const params = new URLSearchParams({ redirect: route.fullPath });
    return `/signin?${params.toString()}`;
  });

  const loadPlans = async () => {
    isLoadingPlans.value = true;
    error.value = '';
    try {
      const response = await BillingService.listPlans();
      plans.value = response.plans;
    } catch (err) {
      const classified = classifyError(err);
      error.value = classified.message || 'Failed to load plans';
      console.error('[Pricing] Error loading plans:', err);
    } finally {
      isLoadingPlans.value = false;
    }
  };

  onMounted(async () => {
    await loadPlans();
  });
</script>

<template>
  <div class="container mx-auto max-w-6xl px-4 py-8">
    <section aria-labelledby="pricing-title">
      <!-- Header -->
      <div class="mb-8 text-center">
        <h1
          id="pricing-title"
          class="mb-3 text-3xl font-bold text-gray-900 dark:text-gray-100">
          {{ t('web.billing.secure_links_stronger_connections') }}
        </h1>
        <p class="mx-auto max-w-2xl text-lg text-gray-600 dark:text-gray-400">
          {{ t('web.billing.secure_your_brand_and_build_customer_trust_with_') }}
        </p>
      </div>

      <!-- Billing Interval Toggle -->
      <div
        class="mb-8 flex items-center justify-center gap-3"
        role="group"
        aria-label="Billing interval">
        <button
          @click="billingInterval = 'month'"
          :aria-pressed="billingInterval === 'month'"
          :class="[
            'rounded-md px-4 py-2 text-sm font-medium transition-colors',
            billingInterval === 'month'
              ? 'bg-brand-600 text-white dark:bg-brand-500'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
          ]">
          {{ t('web.billing.plans.monthly') }}
        </button>
        <button
          @click="billingInterval = 'year'"
          :aria-pressed="billingInterval === 'year'"
          :class="[
            'rounded-md px-4 py-2 text-sm font-medium transition-colors',
            billingInterval === 'year'
              ? 'bg-brand-600 text-white dark:bg-brand-500'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
          ]">
          {{ t('web.billing.plans.yearly') }}
        </button>
      </div>

      <!-- Error Alert -->
      <BasicFormAlerts
        v-if="error"
        :error="error" />

      <!-- Loading State -->
      <PlanCardSkeleton
        v-if="isLoadingPlans"
        class="py-12" />

      <!-- Free Tier Section (standalone banner mode) -->
      <div
        v-if="freePlanStandalone && freePlan && !isLoadingPlans"
        class="mb-10 rounded-lg border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-900/50">
        <div class="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div>
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ freePlan.name }}
            </h3>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {{ t('web.pricing.free_tier_description') }}
            </p>
          </div>
          <RouterLink
            v-if="signupEnabled && freePlan"
            :to="getSignupUrl(freePlan)"
            class="shrink-0 rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 transition-colors hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-gray-700">
            {{ t('web.pricing.get_started_free') }}
          </RouterLink>
          <span
            v-else
            class="shrink-0 cursor-default rounded-md bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-400 ring-1 ring-inset ring-gray-200 dark:bg-gray-800/50 dark:text-gray-500 dark:ring-gray-700"
            aria-disabled="true">
            {{ t('web.pricing.get_started_free') }}
          </span>
        </div>
      </div>

      <!-- Paid Plan Cards -->
      <div
        v-if="!isLoadingPlans && filteredPlans.length > 0"
        class="mx-auto flex max-w-[1400px] flex-wrap items-stretch justify-center gap-6">
        <PlanCard
          v-for="plan in filteredPlans"
          :key="plan.id"
          :plan="plan"
          :is-recommended="isPlanRecommended(plan)"
          :is-highlighted="isPlanHighlighted(plan)"
          :button-label="getCtaLabel(plan)"
          class="w-full max-w-sm">
          <template #action="{ plan: currentPlan }">
            <RouterLink
              v-if="signupEnabled"
              :to="getSignupUrl(currentPlan)"
              :class="[
                'block w-full rounded-md px-4 py-2 text-center text-sm font-semibold transition-colors',
                isPlanRecommended(currentPlan) || isPlanHighlighted(currentPlan)
                  ? 'bg-brand-600 text-white hover:bg-brand-500 dark:bg-brand-500 dark:hover:bg-brand-400'
                  : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-gray-700',
              ]">
              {{ getCtaLabel(currentPlan) }}
            </RouterLink>
            <!-- Explicit empty slot content prevents PlanCard fallback button -->
            <span v-else ></span>
          </template>
        </PlanCard>
      </div>

      <!-- No Paid Plans Message -->
      <div
        v-else-if="!isLoadingPlans && filteredPlans.length === 0 && !error"
        class="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center dark:border-gray-700 dark:bg-gray-900/50">
        <p class="text-gray-600 dark:text-gray-400">
          {{
            t('web.billing.plans.no_plans_available', {
              interval:
                billingInterval === 'year'
                  ? t('web.billing.plans.yearly').toLowerCase()
                  : t('web.billing.plans.monthly').toLowerCase(),
            })
          }}
        </p>
      </div>

      <!-- Custom Needs Section -->
      <div
        class="mt-12 rounded-lg border border-gray-200 bg-gray-50 p-8 text-center dark:border-gray-700 dark:bg-gray-900/50">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          {{ t('web.billing.plans.custom_needs_title') }}
        </h3>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {{ t('web.billing.plans.custom_needs_description') }}
        </p>
        <div class="mt-4 flex justify-center">
          <FeedbackToggle />
        </div>
      </div>

      <!-- Sign in prompt for existing users -->
      <div
        v-if="signinEnabled"
        class="mt-8 text-center">
        <p class="text-sm text-gray-600 dark:text-gray-400">
          {{ t('web.pricing.already_have_account') }}
          <RouterLink
            :to="signinUrl"
            class="font-medium text-brand-600 hover:text-brand-500 dark:text-brand-400 dark:hover:text-brand-300">
            {{ t('web.pricing.sign_in') }}
          </RouterLink>
        </p>
      </div>
    </section>
  </div>
</template>
